import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Injectable, OnInit} from '@angular/core';
import {environment} from 'src/environments/environment.development';
import {ArchivoItem, CambioTitularReq, EventItem, ProyectoItem, ProyectoTramiteItem} from '../../interfaces/proyecto.interface';
import {catchError, from, map, Observable, of, throwError} from "rxjs";
import {Param} from "../../../shared/interfaces/param-interface";
import {convertDate, isNumeric} from "../../../shared/services/utils-service";
import {
    ActualizarFechasCarnetReq,
    ActualizarFechasMigReq,
    ApiResponse,
    ApiResponse_Error,
    ApiResponse_handleHttpError,
    ApiResponse_handleSuccessResponse,
    ApiResponse_ObtencionGenericaMany,
    ApiResponse_ObtencionGenericaSingle,
    ObtencionGenerica
} from '../components/ver-proyecto/interface/ver-proyecto.interface';
import {ResolutionTemplate, ResolutionTemplateResponse} from '../interfaces/resolution-template.interface';
import {PersonaInfo} from "../../../shared/interfaces/shared-interface";
import {LoginServices} from 'src/app/auth/login/services/login.service';
import { FiltersService } from 'src/app/shared/services/filters-service';
import {FileHash} from "../../../shared/interfaces/file-utils-interface";
import {ArchivosHabilitarRq} from "../../tramites/components/interfaces/tramites.interface";

const apiVucUrl = environment.apiVucUrl

// Puedes poner esto en un archivo de interfaces compartidas
export interface CropData {
    width: number;
    height: number;
    imagePosition: {
        x1: number;
        y1: number;
        x2: number;
        y2: number;
    };
    cropperPosition: {
        x1: number;
        y1: number;
        x2: number;
        y2: number;
    };
    blob?: any;
    objectUrl?: string;
    idProyecto: number;
}

@Injectable({
    providedIn: 'root'
})
export class ProyectoService implements OnInit {
    constructor(
        private http: HttpClient,
        private loginServices: LoginServices,
        private filtersService: FiltersService
    ) {
    }

    ngOnInit(): void {
    }

    /**
     * Consulta las generales de proyecto: por idProyecto o usuario
     */
    getGeneralesProyecto(identificador: string | null | '' | undefined) {
        console.log(`identificador -> ${identificador}`);
        let params: Param[] = [];
        if (isNumeric(identificador)) {
            params.push({key: "proyecto", value: identificador});
            params.push({key: "maxResults", value: "1"});
        } else {
            params.push({key: "usuarios", value: identificador});
            params.push({key: "nombre_no_inicia", value: "RP_"});
            params.push({key: "tipo", value: "1,2"});

            const drange = this.filtersService.selectedRange;
            if (drange && drange.length === 2) {
                const desde = convertDate(drange[0]);
                const hasta = convertDate(drange[1]);
                params.push({ key: "desde", value: desde });
                params.push({ key: "hasta", value: hasta });
            }
        }
        return this.consultarPorParametros(params)
    }

    /**
     * Consulta por proyecto por parametros.
     *
     * Parametros soportados: ProyectosUtil.CONSULTA_POR_PARAMETROS_VALUES_ACL
     */
    consultarPorParametros(params: Param[]) {
        if (params == null || params.length == 0) {
            console.error('Consulta de proyectos con parametros invalidos');
        }
        console.log(`params -> ${JSON.stringify(params)}`);
        let url = apiVucUrl + `api/v1.0/proyectos/consulta`;
        return this.http.post<ProyectoItem[]>(url, params, {observe: 'response'})
    }

    /**
     * Consulta el resumen de trámites vinculados al proyecto. Esta es una consulta simplificada de los trámites por fase vinculados a un proyecto.
     */
    getTramitesProyectos(idProyecto: number) {
        console.log(`idProyecto=${idProyecto}`);
        let url = apiVucUrl + `api/v1.0/proyectos/${idProyecto}/tramites`;
        return this.http.get<ProyectoTramiteItem[]>(url, {observe: 'response'})
            .pipe(
                catchError(this.handleError)
            );
    }

    getArchivosProyecto(idProyecto: number) {
        console.log(`idProyecto=${idProyecto}`);
        let url = apiVucUrl + `api/v1.0/archivos/proyecto/${idProyecto}`;
        return this.http.get<ArchivoItem[]>(url, {observe: 'response'})
            .pipe(
                catchError(this.handleError)
            );
    }

    getSrcFile(hash: string, type: string): string {
        return apiVucUrl + `api/v1.0/archivos/${hash}?type=${type}`;
    }

    getTimeLine(idProyecto: number) {
        console.log(`idProyecto=${idProyecto}`);
        let url = apiVucUrl + `api/v1.0/proyectos/timeline/${idProyecto}`;
        return this.http.get<EventItem[]>(url, {observe: 'response'})
            .pipe(
                catchError(this.handleError)
            );
    }

    /*===============================================================================================*/
    /*OBTENCION DE DATOS PARA LAS TABS --------------------------------------------------------------*/
    /*===============================================================================================*/

    // Consulta el registro profesional por idSolicitud
    getRegistroProfesional_Solicitud(idSolicitud: number): Observable<ApiResponse_ObtencionGenericaSingle> {
        const url = `${environment.apiVucUrl}api/v1.0/registro-profesionales/solicitud/${idSolicitud}`;
        return this.http.get<ObtencionGenerica>(url).pipe(
            map(data => ({
                success: true,
                data
            })),
            catchError((error: HttpErrorResponse) => {
                let apiError: ApiResponse_Error;
                switch (error.status) {
                    case 400:
                        apiError = {
                            code: 400,
                            message: 'Solicitud incorrecta',
                            details: error.error?.message || 'Verifica los datos enviados'
                        };
                        break;
                    case 404:
                        apiError = {
                            code: 404,
                            message: 'No encontrado',
                            details: 'No se encontró información con ese número de documento'
                        };
                        break;
                    case 500:
                        apiError = {
                            code: 500,
                            message: 'Error interno del servidor',
                            details: 'Ocurrió un problema al procesar la solicitud en el servidor'
                        };
                        break;
                    default:
                        apiError = {
                            code: error.status,
                            message: 'Error desconocido',
                            details: error.message
                        };
                }
                return new Observable<ApiResponse<ObtencionGenerica>>(
                    observer => {
                        observer.next({
                            success: false,
                            error: apiError
                        });
                        observer.complete();
                    }
                );
            }),
        )
    }

    // Consulta el tramite del registro profesional por numero de documento
    getRegistroProfesional_Tramite(numeroDocumento: string): Observable<ApiResponse_ObtencionGenericaMany> {
        // Validar el número de documento para que sea diferente de null, vacío, nulo o 00000000-0
        if (numeroDocumento && numeroDocumento.trim() === '00000000-0') {
            return new Observable(observer => {
                observer.next({
                    success: false,
                    error: {
                        code: 400,
                        message: 'Número de documento inválido',
                        details: 'El número de documento no puede ser 00000000-0'
                    }
                });
                observer.complete();
            });
        }
        if (numeroDocumento && !isNumeric(numeroDocumento)) {
            return new Observable(observer => {
                observer.next({
                    success: false,
                    error: {
                        code: 400,
                        message: 'Número de documento inválido',
                        details: 'El número de documento debe ser numérico'
                    }
                });
                observer.complete();
            });
        }
        if (!numeroDocumento || numeroDocumento.trim().length === 0) {
            return new Observable(observer => {
                observer.next({
                    success: false,
                    error: {
                        code: 400,
                        message: 'Número de documento inválido',
                        details: 'El número de documento no puede ser vacío o nulo'
                    }
                });
                observer.complete();
            });
        }
        const url = `${environment.apiVucUrl}api/v1.0/registro-profesionales/buscar?numDocumento=${numeroDocumento}`;
        return this.http.get<ObtencionGenerica[]>(url).pipe(map(data => ({
                success: true,
                data
            })),
            catchError((error: HttpErrorResponse) => {
                const customMessages = {
                    404: {
                        message: 'Trámite no encontrado',
                        details: 'No se encontró ningún trámite con ese ID'
                    }
                };
                const apiError = ApiResponse_handleHttpError(error, customMessages);
                return throwError(() => ({success: false, error: apiError}));
            })
        );
    }

    actualizarFechaVencimiento(data: ActualizarFechasCarnetReq) {
        const url = `${environment.apiVucUrl}api/v1.0/registro-profesionales/actualizar-fechas`;
        return this.http.post(url, data, {observe: 'response'},);
    }

    actualizarFechaMigraciones(payload: ActualizarFechasMigReq) {
        const url = `${environment.apiVucUrl}api/v1.0/registro-profesionales/actualizar-migraciones`;
        return this.http.post(url, payload, {observe: 'response'},);
    }



    /**
     * Obtiene información del carnet asociado a una idSolicitud.
     * @param idSolicitud Identificador único de la solicitud.
     * @returns Observable con ApiResponse conteniendo datos genéricos o un error.
     */
    getCarnetByIdSolicitud(idSolicitud: number): Observable<ApiResponse<ObtencionGenerica>> {
        const url = `${environment.apiVucUrl}api/v1.0/carnet/${idSolicitud}`;
        return this.http.post<ObtencionGenerica>(url, null, {observe: 'response'}).pipe(
            map((response) => {
                return ApiResponse_handleSuccessResponse<ObtencionGenerica>(response)
            }),
            catchError((error: HttpErrorResponse) => {
                const apiError = ApiResponse_handleHttpError(error, {
                    404: {
                        message: 'Carnet no encontrado',
                        details: 'No se encontró información del carnet para esta solicitud'
                    }
                });
                return new Observable<ApiResponse<ObtencionGenerica>>(observer => {
                    observer.next({
                        success: false,
                        error: apiError
                    });
                    observer.complete();
                });
            })
        );
    }

    /**
     * Genera y descarga el PDF del carnet asociado a un idProyecto.
     * @param idProyecto Identificador del proyecto.
     * @param cropData
     * @returns Observable que emite el Blob del PDF o un error.
     */
    generarCarnetPdf(idProyecto: number, cropData: any): Observable<{
        blob: Blob;
        fileName: string
    } | ApiResponse<ObtencionGenerica>> {
        const apiVucUrl = environment.apiVucUrl;
        const url = apiVucUrl + `api/v1.0/carnet/pdf`;
        const token = this.loginServices.session()?.token;
        console.log('Enviando solicitud para generar carnet. :', {
            idProyecto,
            url
        });
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': '*/*',
            'Authorization': `Bearer ${token}`,
            'X-Requested-With': 'XMLHttpRequest',
            'Access-Control-Allow-Origin': '*'
        });
        return this.http
            .post(url,
                {idProyecto, cropData},
                {
                    headers,
                    observe: 'response',
                    responseType: 'blob',
                    withCredentials: false
                })
            .pipe(
                map(response => {
                    console.log('Respuesta recibida:', {
                        status: response.status,
                        contentType: response.headers.get('content-type'),
                        contentDisposition: response.headers.get('content-disposition')
                    });
                    const cd = response.headers.get('content-disposition') ?? '';
                    const filenameRegex = /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i;
                    const match = filenameRegex.exec(cd);
                    const fileName = match ? decodeURIComponent(match[1]) : 'carnet.pdf';
                    return {
                        blob: response.body as Blob,
                        fileName
                    };
                }),
                catchError((err: HttpErrorResponse) => {
                    console.error('Error al generar carnet:', {
                        status: err.status,
                        statusText: err.statusText,
                        error: err.error,
                        headers: err.headers,
                        url
                    });
                    let errorMessage = 'Error desconocido al generar el carnet';
                    let errorDetails = 'Por favor, intente nuevamente más tarde';
                    if (err.status === 0) {
                        errorMessage = 'Error de conexión';
                        errorDetails = 'No se pudo conectar con el servidor.';
                    } else if (err.status === 401) {
                        errorMessage = 'No autorizado';
                        errorDetails = 'Su sesión ha expirado.';
                    } else if (err.status === 404) {
                        errorMessage = 'No encontrado';
                        errorDetails = 'No se encontró información del carnet.';
                    }
                    if (err.error instanceof Blob && err.error.type === 'text/plain') {
                        return from(
                            new Promise<ApiResponse<ObtencionGenerica>>((resolve) => {
                                const reader = new FileReader();
                                reader.onload = () => {
                                    const serverMessage = reader.result as string;
                                    resolve({
                                        success: false,
                                        error: {
                                            code: err.status,
                                            message: errorMessage,
                                            details: serverMessage || errorDetails
                                        }
                                    });
                                };
                                reader.onerror = () => {
                                    resolve({
                                        success: false,
                                        error: {
                                            code: err.status,
                                            message: errorMessage,
                                            details: errorDetails
                                        }
                                    });
                                };
                                reader.readAsText(err.error);
                            })
                        );
                    }
                    return of({
                        success: false,
                        error: {
                            code: err.status,
                            message: errorMessage,
                            details: errorDetails
                        }
                    } as ApiResponse<ObtencionGenerica>);
                })
            );
    }

    private handleError(error: HttpErrorResponse) {
        if (error.status === 0) {
            console.error('Ocurrió un error:', error.error);
        } else {
            console.error(`code: ${error.status}, body: `, error.error);
        }
        return throwError(() => new Error('Ocurrió un error al procesar, intente mas tarde.'));
    }

    getResolutionTemplates(): Observable<ResolutionTemplate[]> {
        const url = `${apiVucUrl}api/v1.0/notificacion/word`;
        return this.http.get<ResolutionTemplateResponse>(url).pipe(
            map((templateData) => templateData.catalogo)
            , catchError(this.handleError)
        );
    }

    getResolutioTemplateById(id: string): Observable<Blob> {
        const url = `${apiVucUrl}api/v1.0/notificacion/word/file/${id}`;
        return this.http.get(url, {responseType: 'blob'}).pipe(
            catchError(this.handleError)
        );
    }

    /**
     * Obtiene la informacion del titular asignado al proyecto
     * @Param idProyecto idProyecto*/
    consultaInfoTitularPorProyecto(idProyecto: number) {
        return this.http.get<PersonaInfo[]>(apiVucUrl + `api/v1.0/proyectos/consulta/titular/${idProyecto}`);
    }

    /**
     * Meto que permite habilitar los requisitos para que realice la subsanacion*/
    actualizarArchivosRq(archivosHabilitarRq: ArchivosHabilitarRq[]) {
        let url = apiVucUrl + `api/v1.0/archivos/habilitar-archivos-rq/`;
        return this.http.put<ArchivosHabilitarRq[]>(url,archivosHabilitarRq, {observe: 'response'})
     }
          
     cambioTitular(payload: CambioTitularReq) {
        let url = apiVucUrl + "api/v1.0/proyectos/titular";
        return this.http.post(url, payload, {observe: 'response'})
    }
}
